import { GUI } from 'lil-gui';
import { mergeDeep } from '../utils/config.js';
import { defaultConfig } from '../config/default-config.js';

/**
 * Klasa zarządzająca GUI dla sceny synth
 */
export class SynthGUI {
    constructor(synthScene, synthConfig) {
        this.synthScene = synthScene;
        this.synthConfig = synthConfig;
        this.gui = null;
        this.statsInterval = null; // Interval dla auto-refresh stats
    }
    
    /**
     * Inicjalizacja panelu kontrolnego GUI
     */
    init() {
        if (this.gui) {
            this.gui.destroy();
        }
        
        this.gui = new GUI({ title: 'Synth Scene Controls', width: 300 });
        this.gui.domElement.style.position = 'fixed';
        this.gui.domElement.style.top = '10px';
        this.gui.domElement.style.right = '10px';
        this.gui.domElement.style.zIndex = '1000';
        this.gui.close();
        
        this.setupCameraControls();
        this.setupRoadControls();
        this.setupBloomControls();
        this.setupSunControls();
        this.setupMountainsControls();
        this.setupRenderControls();
        this.setupActions();
        
        return this.gui;
    }
    
    /**
     * Kontrolki kamery
     */
    setupCameraControls() {
        const cameraFolder = this.gui.addFolder('Camera');
        cameraFolder.add(this.synthConfig.camera, 'speed', 0.1, 2.0, 0.01)
            .name('Speed')
            .onChange(() => this.onConfigChange());
        cameraFolder.add(this.synthConfig.camera, 'fov', 50, 120, 1)
            .name('FOV')
            .onChange(() => this.onConfigChange());
    }
    
    /**
     * Kontrolki drogi
     */
    setupRoadControls() {
        const roadFolder = this.gui.addFolder('Road');
        
        // Przełączanie między wersjami renderera
        if (this.synthConfig.ground.useAltRoadRenderer === undefined) {
            this.synthConfig.ground.useAltRoadRenderer = false;
        }
        roadFolder.add(this.synthConfig.ground, 'useAltRoadRenderer')
            .name('Use Alt Renderer (EdgesGeometry)')
            .onChange(() => {
                // Wymaga przebudowy sceny
                console.log('[GUI] Zmiana renderera drogi wymaga przeładowania strony');
            });
        
        roadFolder.add(this.synthConfig.ground, 'linesCount', 5, 50, 1)
            .name('Lines Count')
            .onChange(() => this.onRoadConfigChange());
        roadFolder.add(this.synthConfig.ground, 'lineWidth', 0.01, 2.5, 0.001)
            .name('Line Width')
            .onChange(() => this.onRoadConfigChange());
        roadFolder.add(this.synthConfig.ground, 'glowIntensity', 0.5, 5.0, 0.1)
            .name('Glow Intensity')
            .onChange(() => this.onRoadConfigChange());
        roadFolder.add(this.synthConfig.ground, 'verticalRandomness', 0, 2.0, 0.1)
            .name('Vertical Randomness')
            .onChange(() => this.onRoadConfigChange());
        roadFolder.add(this.synthConfig.ground, 'blockNewSegments')
            .name('Block New Segments')
            .onChange(() => this.onRoadConfigChange());
        
        // Line Breaks controls
        const breaksFolder = roadFolder.addFolder('Line Breaks');
        breaksFolder.add(this.synthConfig.ground.lineBreaks, 'horizontal')
            .name('Horizontal')
            .onChange(() => this.onRoadConfigChange());
        breaksFolder.add(this.synthConfig.ground.lineBreaks, 'vertical')
            .name('Vertical')
            .onChange(() => this.onRoadConfigChange());
        breaksFolder.add(this.synthConfig.ground.lineBreaks, 'position', ['random', 'even'])
            .name('Position')
            .onChange(() => this.onRoadConfigChange());
        breaksFolder.add(this.synthConfig.ground.lineBreaks, 'strength', 0, 1.0, 0.01)
            .name('Strength')
            .onChange(() => this.onRoadConfigChange());
        breaksFolder.add(this.synthConfig.ground.lineBreaks, 'count', 1, 5, 1)
            .name('Count')
            .onChange(() => this.onRoadConfigChange());
        
        // Statystyki drogi (read-only)
        const statsObj = { 
            segments: 0, 
            lines: 0, 
            points: 0,
            update: () => {
                if (this.synthScene && this.synthScene.road && this.synthScene.road.getStats) {
                    const stats = this.synthScene.road.getStats();
                    statsObj.segments = stats.segments;
                    statsObj.lines = stats.lines;
                    statsObj.points = stats.points;
                }
            }
        };
        statsObj.update(); // Pierwsza aktualizacja
        
        const statsFolder = roadFolder.addFolder('Stats (Elements)');
        statsFolder.add(statsObj, 'segments').name('Segments').listen();
        statsFolder.add(statsObj, 'lines').name('Lines').listen();
        statsFolder.add(statsObj, 'points').name('Points').listen();
        statsFolder.add({ refresh: () => statsObj.update() }, 'refresh').name('🔄 Refresh Stats');
        statsFolder.add({ 
            logToConsole: () => {
                if (this.synthScene && this.synthScene.road && this.synthScene.road.logStats) {
                    this.synthScene.road.logStats();
                }
            }
        }, 'logToConsole').name('📊 Log to Console');
        
        // Auto-refresh stats co sekundę
        this.statsInterval = setInterval(() => {
            statsObj.update();
        }, 1000);
    }
    
    /**
     * Kontrolki bloom
     */
    setupBloomControls() {
        const bloomFolder = this.gui.addFolder('Bloom');
        bloomFolder.add(this.synthConfig.ground.bloom, 'enabled')
            .name('Enabled')
            .onChange(() => this.onConfigChange());
        bloomFolder.add(this.synthConfig.ground.bloom, 'threshold', 0, 1.0, 0.01)
            .name('Threshold')
            .onChange(() => this.onConfigChange());
        bloomFolder.add(this.synthConfig.ground.bloom, 'strength', 0, 3.0, 0.1)
            .name('Strength')
            .onChange(() => this.onConfigChange());
        bloomFolder.add(this.synthConfig.ground.bloom, 'radius', 0, 2.0, 0.1)
            .name('Radius')
            .onChange(() => this.onConfigChange());
    }
    
    /**
     * Kontrolki słońca
     */
    setupSunControls() {
        const sunFolder = this.gui.addFolder('Sun');
        sunFolder.add(this.synthConfig.sun, 'size', 10, 200, 1)
            .name('Size')
            .onChange(() => this.onConfigChange());
        sunFolder.add(this.synthConfig.sun, 'positionX', -1, 1, 0.01)
            .name('Position X')
            .onChange(() => this.onConfigChange());
        sunFolder.add(this.synthConfig.sun, 'positionY', -1, 1, 0.01)
            .name('Position Y')
            .onChange(() => this.onConfigChange());
        
        const sunColorObj = { 
            color: '#' + this.synthConfig.sun.color.toString(16).padStart(6, '0') 
        };
        sunFolder.addColor(sunColorObj, 'color')
            .name('Color')
            .onChange(() => {
                this.synthConfig.sun.color = parseInt(sunColorObj.color.replace('#', ''), 16);
                this.onConfigChange();
            });
    }
    
    /**
     * Kontrolki gór po bokach drogi
     */
    setupMountainsControls() {
        if (!this.synthConfig.mountains) return;
        const mountainsFolder = this.gui.addFolder('Mountains');

        mountainsFolder.add(this.synthConfig.mountains, 'enabled')
            .name('Enabled')
            .onChange(() => this.onConfigChange());

        mountainsFolder.add(this.synthConfig.mountains, 'solid')
            .name('Solid (no see-through)')
            .onChange(() => this.onConfigChange());

        const fillColorObj = {
            color: '#' + (this.synthConfig.mountains.fillColor ?? 0x000000).toString(16).padStart(6, '0')
        };
        mountainsFolder.addColor(fillColorObj, 'color')
            .name('Fill Color')
            .onChange(() => {
                this.synthConfig.mountains.fillColor = parseInt(fillColorObj.color.replace('#', ''), 16);
                this.onConfigChange();
            });

        mountainsFolder.add(this.synthConfig.mountains, 'height', 5, 120, 1)
            .name('Height')
            .onChange(() => this.onConfigChange());
        mountainsFolder.add(this.synthConfig.mountains, 'stripWidth', 20, 200, 1)
            .name('Strip Width')
            .onChange(() => this.onConfigChange());
        mountainsFolder.add(this.synthConfig.mountains, 'noiseScale', 0.01, 0.2, 0.005)
            .name('Noise Scale')
            .onChange(() => this.onConfigChange());
    }

    /**
     * Kontrolki renderowania
     */
    setupRenderControls() {
        const renderFolder = this.gui.addFolder('Render');
        renderFolder.add(this.synthConfig.render, 'resolutionScale', 0.1, 1.0, 0.05)
            .name('Resolution Scale')
            .onChange(() => this.onConfigChange());
    }
    
    /**
     * Akcje (reset, export)
     */
    setupActions() {
        // Reset to defaults
        this.gui.add({ reset: () => this.resetToDefaults() }, 'reset')
            .name('Reset to Defaults');
        
        // Export to JSON
        this.gui.add({ exportJSON: () => this.exportToJSON() }, 'exportJSON')
            .name('📋 Export to JSON');
        
        // Close button
        this.gui.add({ close: () => this.close() }, 'close')
            .name('Close Controls');
    }
    
    /**
     * Callback przy zmianie konfiguracji
     */
    onConfigChange() {
        if (this.synthScene) {
            this.synthScene.updateConfig(this.synthConfig);
        }
    }
    
    /**
     * Callback przy zmianie konfiguracji drogi
     */
    onRoadConfigChange() {
        if (this.synthScene && this.synthScene.road) {
            this.synthScene.road.updateConfig(this.synthConfig.ground);
        }
    }
    
    /**
     * Reset do wartości domyślnych
     */
    resetToDefaults() {
        Object.assign(this.synthConfig, mergeDeep({}, defaultConfig, {
            camera: { speed: 0.23, fov: 75 },
            ground: { 
                linesCount: 20, 
                lineWidth: 0.099, 
                glowIntensity: 1.5,
                bloom: { enabled: true, threshold: 0.1, strength: 0.5, radius: 0.4 }
            },
            sun: { size: 80, positionX: 0, positionY: 0.3, color: 0x9510FC },
            render: { resolutionScale: 0.35 }
        }));
        this.gui.updateDisplay();
        this.onConfigChange();
    }
    
    /**
     * Eksport konfiguracji do JSON
     */
    exportToJSON() {
        const configToExport = JSON.parse(JSON.stringify(this.synthConfig));
        configToExport.sun.color = '0x' + this.synthConfig.sun.color.toString(16).padStart(6, '0').toUpperCase();
        const jsonString = JSON.stringify(configToExport, null, 4);
        
        navigator.clipboard.writeText(jsonString).then(() => {
            console.log('%c✅ Konfiguracja skopiowana do schowka!', 'color: green; font-weight: bold; font-size: 14px;');
            console.log('%c📋 Wklej poniższą konfigurację do main.js jako synthConfig:', 'color: blue; font-weight: bold;');
            console.log(jsonString);
            alert('✅ Konfiguracja skopiowana do schowka!\n\nSprawdź konsolę (F12) aby zobaczyć JSON.');
        }).catch(err => {
            console.error('Błąd kopiowania do schowka:', err);
            console.log('%c📋 Konfiguracja JSON:', 'color: blue; font-weight: bold; font-size: 14px;');
            console.log(jsonString);
            prompt('Skopiuj konfigurację (Ctrl+C):', jsonString);
        });
    }
    
    /**
     * Zamknij GUI
     */
    close() {
        if (this.gui) {
            this.gui.hide();
        }
    }
    
    /**
     * Zniszcz GUI
     */
    destroy() {
        // Zatrzymaj auto-refresh stats
        if (this.statsInterval) {
            clearInterval(this.statsInterval);
            this.statsInterval = null;
        }
        
        if (this.gui) {
            this.gui.destroy();
            this.gui = null;
        }
    }
}

